import './WebChatPluginPopup.scss';
import { ChatWithBotWidget } from './ChatWithBotWidget';
import React, { useState, useEffect, useCallback, useMemo } from 'react';

export function WebChatPluginPopup({
  conversation,
  webChatConfiguration,
  preloadedImageDimensions,
  showResetButton,
  onResetClicked,
}) {
  const isMobile = window.innerWidth <= 768;
  // Auto-fit mode requires BOTH the mode setting AND an actual background image.
  // Without an image, auto-fit is meaningless and would only strip padding (bug #005).
  const backgroundImageUrl = webChatConfiguration?.background_watermark_url;
  const isAutoFit =
    webChatConfiguration?.background_image_mode === 'auto-fit' &&
    !!backgroundImageUrl;

  // Compute initial auto-fit size synchronously from preloaded image dimensions.
  // This eliminates the layout shift ("larger then shrinks") by providing the
  // correct popup size on the very first render frame.
  const initialAutoFitSize = useMemo(() => {
    if (!isAutoFit || !preloadedImageDimensions) return null;
    const { naturalWidth, naturalHeight } = preloadedImageDimensions;
    if (naturalWidth <= 0 || naturalHeight <= 0) return null;
    const popupRightMargin = 10;
    const popupBottomMargin = 100;
    const maxW = window.innerWidth - popupRightMargin;
    const maxH = window.innerHeight - popupBottomMargin;
    const scale = Math.min(maxW / naturalWidth, maxH / naturalHeight, 1);
    return {
      width: Math.floor(naturalWidth * scale),
      height: Math.floor(naturalHeight * scale),
    };
  }, [isAutoFit, preloadedImageDimensions]);

  const [autoFitPopupSize, setAutoFitPopupSize] = useState(initialAutoFitSize);

  useEffect(() => {
    if (!isAutoFit || !backgroundImageUrl) {
      setAutoFitPopupSize(null);
      return;
    }

    let cancelled = false;
    let imgW = 0,
      imgH = 0;

    const recalculate = () => {
      if (cancelled || imgW <= 0 || imgH <= 0) return;
      const popupRightMargin = 10;
      const popupBottomMargin = 100;
      const maxW = window.innerWidth - popupRightMargin;
      const maxH = window.innerHeight - popupBottomMargin;
      const scale = Math.min(maxW / imgW, maxH / imgH, 1);
      setAutoFitPopupSize({
        width: Math.floor(imgW * scale),
        height: Math.floor(imgH * scale),
      });
    };

    const img = new window.Image();
    img.onload = () => {
      imgW = img.naturalWidth;
      imgH = img.naturalHeight;
      recalculate();
    };
    img.onerror = () => {
      if (!cancelled) setAutoFitPopupSize(null);
    };
    img.src = backgroundImageUrl;

    window.addEventListener('resize', recalculate);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', recalculate);
    };
  }, [isAutoFit, backgroundImageUrl]);

  const popupStyle = useMemo(() => {
    if (isAutoFit && autoFitPopupSize) {
      return {
        '--prospera-chat-max-width': `${autoFitPopupSize.width}px`,
        '--prospera-chat-max-height': `${autoFitPopupSize.height}px`,
      };
    }

    return {
      ...(webChatConfiguration?.chat_window_max_width
        ? {
            '--prospera-chat-max-width': `${webChatConfiguration.chat_window_max_width}px`,
          }
        : {}),
      ...(webChatConfiguration?.chat_window_max_height
        ? {
            '--prospera-chat-max-height': `${webChatConfiguration.chat_window_max_height}px`,
          }
        : {}),
    };
  }, [
    isAutoFit,
    autoFitPopupSize,
    webChatConfiguration?.chat_window_max_width,
    webChatConfiguration?.chat_window_max_height,
  ]);

  const stopEventPropagation = useCallback(
    (e) => {
      if (isMobile) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [isMobile]
  );

  const overlayClass = `prospera-web-chat-plugin-popup-overlay ${
    isMobile ? 'mobile' : ''
  }`;

  return (
    <>
      {isMobile ? (
        <div
          className={overlayClass}
          onClick={stopEventPropagation}
          onScroll={stopEventPropagation}
        />
      ) : null}
      <div className="prospera-web-chat-plugin-popup" style={popupStyle}>
        <ChatWithBotWidget
          conversation={conversation}
          webChatConfiguration={webChatConfiguration}
          showResetButton={showResetButton}
          onResetClicked={onResetClicked}
          showInternalEvents={false}
          showBoundingBox={true}
        />
      </div>
    </>
  );
}
